import { FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createAdminServer,
  deleteAdminServer,
  fetchAdminServers,
  fetchServerRaidContent,
  initializeServerRaidContent,
  updateAdminServer,
  updateServerExpansionState,
  updateServerRaidTierState,
  updateServerRankingPartitionState,
  type ServerContentStatePayload,
  type ServerFormPayload,
} from "../api/adminServers";
import type { ServerRaidContentState, ServerRecord } from "../api/types";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";

interface ServerFormState {
  id: number | null;
  slug: string;
  name: string;
  aliasesText: string;
  isActive: boolean;
}

const emptyForm: ServerFormState = {
  id: null,
  slug: "",
  name: "",
  aliasesText: "",
  isActive: true,
};

function aliasesFromText(value: string) {
  return value
    .split(/[,\n]/)
    .map((alias) => alias.trim())
    .filter(Boolean);
}

function formFromServer(server: ServerRecord): ServerFormState {
  return {
    id: server.id,
    slug: server.slug,
    name: server.name,
    aliasesText: server.aliases.join("\n"),
    isActive: server.isActive,
  };
}

function payloadFromForm(form: ServerFormState): ServerFormPayload {
  return {
    slug: form.slug.trim(),
    name: form.name.trim(),
    aliases: aliasesFromText(form.aliasesText),
    isActive: form.isActive,
  };
}

function formatDateTimeLocal(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function dateTimeLocalToIso(value: string) {
  if (!value.trim()) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

interface PartitionEditState {
  isActive: boolean;
  startsAt: string;
  endsAt: string;
  isFrozen: boolean;
  notes: string;
}

function partitionStateFromContent(content: ServerRaidContentState | null) {
  const state: Record<string, PartitionEditState> = {};

  for (const partition of content?.rankingPartitions ?? []) {
    state[partition.rankingPartitionId] = {
      isActive: partition.isActive,
      startsAt: formatDateTimeLocal(partition.startsAt),
      endsAt: formatDateTimeLocal(partition.endsAt),
      isFrozen: partition.isFrozen,
      notes: partition.notes ?? "",
    };
  }

  return state;
}

function partitionPayloadFromState(
  state: PartitionEditState,
): ServerContentStatePayload {
  return {
    isActive: state.isActive,
    startsAt: dateTimeLocalToIso(state.startsAt),
    endsAt: dateTimeLocalToIso(state.endsAt),
    isFrozen: state.isFrozen,
    notes: state.notes.trim() || null,
  };
}

export function AdminServersPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ServerFormState>(emptyForm);
  const [selectedContentServerId, setSelectedContentServerId] = useState<
    number | null
  >(null);

  const serversQuery = useQuery({
    queryKey: ["admin", "servers"],
    queryFn: fetchAdminServers,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: ServerFormPayload) => {
      if (form.id === null) {
        return createAdminServer(payload);
      }

      return updateAdminServer(form.id, payload);
    },
    onSuccess: async () => {
      setForm(emptyForm);
      await queryClient.invalidateQueries({ queryKey: ["admin", "servers"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAdminServer,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "servers"] });
    },
  });

  const sortedServers = useMemo(() => {
    return [...(serversQuery.data ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [serversQuery.data]);

  const raidContentQuery = useQuery({
    queryKey: ["admin", "servers", selectedContentServerId, "raid-content"],
    queryFn: () => fetchServerRaidContent(selectedContentServerId!),
    enabled: selectedContentServerId !== null,
  });

  const initializeContentMutation = useMutation({
    mutationFn: initializeServerRaidContent,
    onSuccess: async (_content, serverId) => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "servers", serverId, "raid-content"],
      });
    },
  });

  const updateExpansionStateMutation = useMutation({
    mutationFn: ({
      serverId,
      expansionId,
      isActive,
    }: {
      serverId: number;
      expansionId: string;
      isActive: boolean;
    }) => updateServerExpansionState(serverId, expansionId, { isActive }),
    onSuccess: async (_content, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "servers", variables.serverId, "raid-content"],
      });
    },
  });

  const updateTierStateMutation = useMutation({
    mutationFn: ({
      serverId,
      raidTierId,
      isActive,
    }: {
      serverId: number;
      raidTierId: string;
      isActive: boolean;
    }) => updateServerRaidTierState(serverId, raidTierId, { isActive }),
    onSuccess: async (_content, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "servers", variables.serverId, "raid-content"],
      });
    },
  });

  const updatePartitionStateMutation = useMutation({
    mutationFn: ({
      serverId,
      rankingPartitionId,
      payload,
    }: {
      serverId: number;
      rankingPartitionId: string;
      payload: ServerContentStatePayload;
    }) =>
      updateServerRankingPartitionState(serverId, rankingPartitionId, payload),
    onSuccess: async (_content, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "servers", variables.serverId, "raid-content"],
      });
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    saveMutation.mutate(payloadFromForm(form));
  }

  return (
    <div className="container-fluid px-3 px-lg-4 py-4">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h2 mb-1">Server Management</h1>
          <p className="text-body-secondary mb-0">
            Manage canonical EverQuest server records used to resolve fzClient
            uploads. Client uploads never create servers automatically.
          </p>
        </div>
        <button
          className="btn btn-outline-secondary btn-sm"
          type="button"
          onClick={() => void serversQuery.refetch()}
          disabled={false}
        >
          <i className="bi bi-arrow-clockwise me-1" /> Refresh
        </button>
      </div>

      <div className="alert alert-info" role="alert">
        <div className="fw-semibold">Session-protected admin area</div>
        <div>
          Server records are CMS-managed reference data. Client uploads resolve
          against these records and never create servers automatically.
        </div>
      </div>

      <div className="row g-4">
        <div className="col-xl-4">
          <div className="card shadow-sm">
            <div className="card-header fw-semibold">
              {form.id === null ? "Add Server" : "Edit Server"}
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="serverName">
                    Name
                  </label>
                  <input
                    id="serverName"
                    className="form-control"
                    value={form.name}
                    onChange={(event) =>
                      setForm({ ...form, name: event.target.value })
                    }
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="serverSlug">
                    Slug
                  </label>
                  <input
                    id="serverSlug"
                    className="form-control"
                    value={form.slug}
                    onChange={(event) =>
                      setForm({ ...form, slug: event.target.value })
                    }
                    required
                  />
                  <div className="form-text">
                    Canonical lowercase id, for example <code>fangbreaker</code>
                    .
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="serverAliases">
                    Aliases
                  </label>
                  <textarea
                    id="serverAliases"
                    className="form-control"
                    rows={5}
                    value={form.aliasesText}
                    onChange={(event) =>
                      setForm({ ...form, aliasesText: event.target.value })
                    }
                    placeholder="Fangbreaker\nfang breaker"
                  />
                  <div className="form-text">
                    One alias per line, or comma separated.
                  </div>
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    id="serverIsActive"
                    className="form-check-input"
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(event) =>
                      setForm({ ...form, isActive: event.target.checked })
                    }
                  />
                  <label className="form-check-label" htmlFor="serverIsActive">
                    Active
                  </label>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={saveMutation.isPending}
                  >
                    {form.id === null ? "Create server" : "Save changes"}
                  </button>
                  {form.id !== null ? (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setForm(emptyForm)}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
                {saveMutation.isError ? (
                  <div className="text-danger small mt-3">
                    {saveMutation.error.message}
                  </div>
                ) : null}
              </form>
            </div>
          </div>
        </div>

        <div className="col-xl-8">
          {serversQuery.isLoading ? (
            <LoadingState label="Loading servers" />
          ) : null}
          {serversQuery.isError ? (
            <ErrorState
              title="Could not load servers"
              error={serversQuery.error}
            />
          ) : null}

          {serversQuery.data ? (
            <div className="card shadow-sm">
              <div className="table-responsive">
                <table className="table table-dark table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Slug</th>
                      <th scope="col">Aliases</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedServers.map((server) => (
                      <tr key={server.id}>
                        <td className="fw-semibold">{server.name}</td>
                        <td>
                          <code>{server.slug}</code>
                        </td>
                        <td>
                          {server.aliases.length === 0 ? (
                            <span className="text-body-secondary">None</span>
                          ) : (
                            <div className="d-flex flex-wrap gap-1">
                              {server.aliases.map((alias) => (
                                <span
                                  className="badge text-bg-secondary"
                                  key={alias}
                                >
                                  {alias}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          {server.isActive ? (
                            <span className="badge text-bg-success">
                              Active
                            </span>
                          ) : (
                            <span className="badge text-bg-secondary">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-info"
                              type="button"
                              onClick={() =>
                                setSelectedContentServerId(server.id)
                              }
                            >
                              Raid content
                            </button>
                            <button
                              className="btn btn-outline-light"
                              type="button"
                              onClick={() => setForm(formFromServer(server))}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              type="button"
                              disabled={deleteMutation.isPending}
                              onClick={() => {
                                if (window.confirm(`Delete ${server.name}?`)) {
                                  deleteMutation.mutate(server.id);
                                }
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {selectedContentServerId !== null ? (
        <div className="mt-4">
          <ServerRaidContentPanel
            content={raidContentQuery.data ?? null}
            loading={raidContentQuery.isLoading}
            error={raidContentQuery.error}
            serverId={selectedContentServerId}
            onInitialize={() =>
              initializeContentMutation.mutate(selectedContentServerId)
            }
            onToggleExpansion={(expansionId, isActive) =>
              updateExpansionStateMutation.mutate({
                serverId: selectedContentServerId,
                expansionId,
                isActive,
              })
            }
            onToggleTier={(raidTierId, isActive) =>
              updateTierStateMutation.mutate({
                serverId: selectedContentServerId,
                raidTierId,
                isActive,
              })
            }
            partitionSaving={updatePartitionStateMutation.isPending}
            onUpdatePartition={(rankingPartitionId, payload) =>
              updatePartitionStateMutation.mutate({
                serverId: selectedContentServerId,
                rankingPartitionId,
                payload,
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function ServerRaidContentPanel({
  content,
  loading,
  error,
  serverId,
  onInitialize,
  onToggleExpansion,
  onToggleTier,
  partitionSaving,
  onUpdatePartition,
}: {
  content: ServerRaidContentState | null;
  loading: boolean;
  error: Error | null;
  serverId: number;
  onInitialize: () => void;
  onToggleExpansion: (expansionId: string, isActive: boolean) => void;
  onToggleTier: (raidTierId: string, isActive: boolean) => void;
  partitionSaving: boolean;
  onUpdatePartition: (
    rankingPartitionId: string,
    payload: ServerContentStatePayload,
  ) => void;
}) {
  const [partitionEdits, setPartitionEdits] = useState<
    Record<string, PartitionEditState>
  >({});

  useEffect(() => {
    setPartitionEdits(partitionStateFromContent(content));
  }, [content]);
  if (loading) return <LoadingState label="Loading server raid content" />;
  if (error)
    return (
      <ErrorState title="Could not load server raid content" error={error} />
    );

  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Server Raid Availability</div>
          <div className="small text-body-secondary">
            Toggle which global raid content is active for this server.
            Expansion state cascades to child tiers.
          </div>
        </div>
        <button
          className="btn btn-sm btn-outline-primary"
          type="button"
          onClick={onInitialize}
        >
          Initialize missing rows
        </button>
      </div>
      <div className="card-body">
        {!content ? (
          <div className="alert alert-secondary mb-0">
            Select Initialize missing rows to create server-scoped availability
            records.
          </div>
        ) : (
          <div className="row g-4">
            <div className="col-xl-5">
              <h3 className="h6 text-uppercase text-body-secondary">
                Expansions
              </h3>
              <div className="list-group">
                {content.expansions.map((expansion) => (
                  <label
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    key={expansion.expansionId}
                  >
                    <span>
                      <span className="fw-semibold">{expansion.name}</span>
                      <span className="d-block small text-body-secondary">
                        {expansion.effectivelyActive
                          ? "Active for this server"
                          : "Inactive for this server"}
                      </span>
                    </span>
                    <input
                      className="form-check-input ms-3"
                      type="checkbox"
                      checked={expansion.isActive}
                      onChange={(event) =>
                        onToggleExpansion(
                          expansion.expansionId,
                          event.target.checked,
                        )
                      }
                    />
                  </label>
                ))}
              </div>
            </div>
            <div className="col-xl-7">
              <h3 className="h6 text-uppercase text-body-secondary">
                Raid Tiers
              </h3>
              <div className="table-responsive">
                <table className="table table-dark table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Tier</th>
                      <th>Expansion</th>
                      <th>Effective</th>
                      <th className="text-end">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {content.raidTiers.map((tier) => (
                      <tr key={tier.raidTierId}>
                        <td className="fw-semibold">{tier.name}</td>
                        <td>{tier.expansion.name}</td>
                        <td>
                          {tier.effectivelyActive ? (
                            <span className="badge text-bg-success">Shown</span>
                          ) : (
                            <span className="badge text-bg-secondary">
                              Hidden
                            </span>
                          )}
                        </td>
                        <td className="text-end">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={tier.isActive}
                            onChange={(event) =>
                              onToggleTier(
                                tier.raidTierId,
                                event.target.checked,
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-text mt-2">
                A tier can only be effectively shown when both the tier and its
                expansion are active for server #{serverId}.
              </div>
            </div>

            <div className="col-12">
              <h3 className="h6 text-uppercase text-body-secondary">
                Ranking Partitions
              </h3>
              <div className="table-responsive">
                <table className="table table-dark table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Partition</th>
                      <th>Raid Tier</th>
                      <th style={{ minWidth: 180 }}>Starts</th>
                      <th style={{ minWidth: 180 }}>Ends</th>
                      <th>Frozen</th>
                      <th>Active</th>
                      <th style={{ minWidth: 220 }}>Notes</th>
                      <th className="text-end">Save</th>
                    </tr>
                  </thead>
                  <tbody>
                    {content.rankingPartitions.map((partition) => {
                      const edit = partitionEdits[
                        partition.rankingPartitionId
                      ] ?? {
                        isActive: partition.isActive,
                        startsAt: formatDateTimeLocal(partition.startsAt),
                        endsAt: formatDateTimeLocal(partition.endsAt),
                        isFrozen: partition.isFrozen,
                        notes: partition.notes ?? "",
                      };

                      return (
                        <tr key={partition.rankingPartitionId}>
                          <td className="fw-semibold">
                            {partition.name}
                            {partition.globalIsDefault ? (
                              <span className="badge text-bg-info ms-2">
                                Default
                              </span>
                            ) : null}
                            {partition.effectivelyFrozen ? (
                              <span className="badge text-bg-warning ms-2">
                                Frozen
                              </span>
                            ) : null}
                          </td>
                          <td>{partition.raidTier?.name ?? "No raid tier"}</td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              type="datetime-local"
                              value={edit.startsAt}
                              onChange={(event) =>
                                setPartitionEdits((current) => ({
                                  ...current,
                                  [partition.rankingPartitionId]: {
                                    ...edit,
                                    startsAt: event.target.value,
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              type="datetime-local"
                              value={edit.endsAt}
                              onChange={(event) =>
                                setPartitionEdits((current) => ({
                                  ...current,
                                  [partition.rankingPartitionId]: {
                                    ...edit,
                                    endsAt: event.target.value,
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={edit.isFrozen}
                              onChange={(event) =>
                                setPartitionEdits((current) => ({
                                  ...current,
                                  [partition.rankingPartitionId]: {
                                    ...edit,
                                    isFrozen: event.target.checked,
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={edit.isActive}
                              onChange={(event) =>
                                setPartitionEdits((current) => ({
                                  ...current,
                                  [partition.rankingPartitionId]: {
                                    ...edit,
                                    isActive: event.target.checked,
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              value={edit.notes}
                              onChange={(event) =>
                                setPartitionEdits((current) => ({
                                  ...current,
                                  [partition.rankingPartitionId]: {
                                    ...edit,
                                    notes: event.target.value,
                                  },
                                }))
                              }
                            />
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              type="button"
                              disabled={partitionSaving}
                              onClick={() =>
                                onUpdatePartition(
                                  partition.rankingPartitionId,
                                  partitionPayloadFromState(edit),
                                )
                              }
                            >
                              Save
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="form-text mt-2">
                Ranking partition windows are server-specific. Uploads and
                rankings use the active partition whose date window contains the
                encounter date.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
